import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  History,
  Users,
  Building2,
  MapPin,
  Clock,
  FileBarChart2,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  CalendarDays,
  ClipboardCheck,
  ClipboardList,
  Timer,
  FileText,
  CalendarCheck,
  SlidersHorizontal,
  MapPinOff,
  HeartPulse,
  ReceiptText,
  UserCog,
  Megaphone,
  CalendarOff,
  Coins,
  Wallet,
  Calculator,
  Sparkles,
  Gauge,
  IdCard,
  Table2,
  Layers3,
  Landmark,
  Plane,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useIdleLogout } from "@/hooks/useIdleLogout";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const NAV_EMPLOYEE = [
  { to: "/", label: "Hari Ini", icon: LayoutDashboard, testId: "nav-today" },
  { to: "/riwayat", label: "Riwayat", icon: History, testId: "nav-history" },
  { to: "/cuti-izin", label: "Cuti & Izin", icon: CalendarDays, testId: "nav-cuti-izin" },
  { to: "/lembur", label: "Lembur", icon: Timer, testId: "nav-lembur", key: "lembur" },
  { to: "/klaim", label: "Klaim Pengobatan", icon: HeartPulse, testId: "nav-klaim" },
  { to: "/slip-gaji", label: "Slip Gaji", icon: Wallet, testId: "nav-slip-gaji" },
  { to: "/biodata", label: "Biodata Saya", icon: IdCard, testId: "nav-biodata" },
];

const NAV_ADMIN = [
  { to: "/admin/summary-harian", label: "Summary Harian", icon: ClipboardCheck, testId: "nav-summary-harian" },
  {
    label: "Laporan",
    icon: FileBarChart2,
    testId: "nav-laporan-group",
    children: [
      { to: "/admin/laporan", label: "Laporan Absensi", icon: FileBarChart2, testId: "nav-laporan" },
      { to: "/admin/laporan-lembur", label: "Laporan Lembur", icon: FileText, testId: "nav-laporan-lembur" },
      { to: "/admin/laporan-klaim", label: "Laporan Klaim", icon: ReceiptText, testId: "nav-laporan-klaim" },
      { to: "/admin/laporan-payroll", label: "Laporan Gaji", icon: Wallet, testId: "nav-laporan-payroll" },
      { to: "/admin/demografi", label: "Demografi Pegawai", icon: Users, testId: "nav-demografi" },
    ],
  },
  { to: "/admin/payroll-run", label: "Run Payroll", icon: Calculator, testId: "nav-payroll-run" },
  { to: "/admin/saldo-cuti", label: "Saldo Cuti", icon: CalendarCheck, testId: "nav-saldo-cuti" },
  { to: "/admin/pengajuan", label: "Semua Pengajuan", icon: ClipboardList, testId: "nav-admin-pengajuan" },
  {
    label: "Master Pegawai",
    icon: Users,
    testId: "nav-pegawai-group",
    children: [
      { to: "/admin/pegawai", label: "Daftar Pegawai", icon: Users, testId: "nav-pegawai" },
      { to: "/admin/biodata", label: "Database Pegawai", icon: IdCard, testId: "nav-admin-biodata" },
      { to: "/admin/bulk-nominal", label: "Update Nominal Massal", icon: Table2, testId: "nav-bulk-nominal" },
      { to: "/admin/komponen-tidak-tetap", label: "Komponen Tidak Tetap", icon: Sparkles, testId: "nav-tidak-tetap" },
    ],
  },
  {
    label: "Organisasi",
    icon: Building2,
    testId: "nav-organisasi-group",
    children: [
      { to: "/admin/organisasi", label: "Struktur Organisasi", icon: Building2, testId: "nav-organisasi" },
      { to: "/admin/cost-center", label: "Cost Center", icon: Layers3, testId: "nav-cost-center" },
      { to: "/admin/koperasi", label: "Koperasi", icon: Landmark, testId: "nav-koperasi" },
      { to: "/admin/report-groups", label: "Grup Laporan", icon: Layers3, testId: "nav-report-groups" },
    ],
  },
  {
    label: "Konfigurasi Kebijakan",
    icon: SlidersHorizontal,
    testId: "nav-konfigurasi-group",
    children: [
      { to: "/admin/konfigurasi", label: "Kebijakan Umum", icon: SlidersHorizontal, testId: "nav-konfigurasi" },
      { to: "/admin/master-kepegawaian", label: "Master Data Kepegawaian", icon: IdCard, testId: "nav-master-kepegawaian" },
      { to: "/admin/konfigurasi-sppd", label: "Perjalanan Dinas (SPPD)", icon: Plane, testId: "nav-konfigurasi-sppd" },
      { to: "/admin/komponen-gaji", label: "Master Komponen", icon: Coins, testId: "nav-komponen-gaji" },
      { to: "/admin/wfa-bulk", label: "WFA Bulk", icon: MapPinOff, testId: "nav-wfa-bulk" },
      { to: "/admin/lokasi", label: "Lokasi Kerja", icon: MapPin, testId: "nav-lokasi" },
      { to: "/admin/jadwal", label: "Jadwal Kerja", icon: Clock, testId: "nav-jadwal" },
      { to: "/admin/holidays", label: "Hari Libur Nasional", icon: CalendarOff, testId: "nav-holidays" },
    ],
  },
];

const LEADER_ROLES = new Set(["admin", "manager", "asisten_manager", "general_manager", "kepala_divisi", "direksi"]);
// Fase 7: role yang TIDAK boleh melihat menu Lembur (pimpinan).
const HIDE_LEMBUR_ROLES = new Set(["asisten_manager", "manager", "general_manager", "kepala_divisi", "direksi"]);
// Fase 12: Atasan Unit — akses monitoring & laporan terbatas pada unit kerjanya.
const ATASAN_UNIT_ROLES = new Set(["asisten_manager", "manager"]);

// Menu khusus Atasan Unit (data otomatis terkunci ke Unit Kerja miliknya).
const NAV_ATASAN = [
  { to: "/atasan/kuota-lembur", label: "Kuota Lembur Tim", icon: Gauge, testId: "nav-kuota-lembur-unit" },
  { to: "/admin/laporan", label: "Laporan Absensi", icon: FileBarChart2, testId: "nav-laporan" },
  { to: "/admin/laporan-lembur", label: "Laporan Lembur", icon: FileText, testId: "nav-laporan-lembur" },
];

// Menu khusus Payroll Admin (Pegawai & Payroll, tanpa aksi hapus).
const NAV_PAYROLL_ADMIN = [
  { to: "/admin/payroll-run", label: "Run Payroll", icon: Calculator, testId: "nav-payroll-run" },
  { to: "/admin/laporan-payroll", label: "Laporan Gaji", icon: Wallet, testId: "nav-laporan-payroll" },
  { to: "/admin/pegawai", label: "Master Pegawai", icon: Users, testId: "nav-pegawai" },
  { to: "/admin/komponen-gaji", label: "Master Komponen", icon: Coins, testId: "nav-komponen-gaji" },
  { to: "/admin/bulk-nominal", label: "Update Nominal Massal", icon: Table2, testId: "nav-bulk-nominal" },
  { to: "/admin/komponen-tidak-tetap", label: "Komponen Tidak Tetap", icon: Sparkles, testId: "nav-tidak-tetap" },
];

function CountBadge({ count }) {
  if (!count) return null;
  return (
    <span
      className="ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-600 px-1.5 text-[10px] font-bold text-white"
      data-testid="approval-count-badge"
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

function RoleBadge({ role }) {
  const map = {
    admin: "bg-teal-700 text-white",
    payroll_admin: "bg-amber-600 text-white",
    direksi: "bg-purple-600 text-white",
    kepala_divisi: "bg-indigo-600 text-white",
    manager: "bg-blue-600 text-white",
    asisten_manager: "bg-sky-600 text-white",
    staff: "bg-slate-700 text-white",
  };
  return (
    <span
      className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${map[role] || "bg-slate-500 text-white"}`}
      data-testid="role-badge"
    >
      {role?.replace("_", " ")}
    </span>
  );
}

function NavGroup({ item, onNavigate }) {
  const location = useLocation();
  const Icon = item.icon;
  const childActive = item.children.some(
    (c) => location.pathname === c.to || (c.to !== "/" && location.pathname.startsWith(c.to))
  );
  const [open, setOpen] = useState(childActive);
  useEffect(() => {
    if (childActive) setOpen(true);
  }, [childActive]);

  return (
    <div data-testid={`${item.testId}-wrap`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={item.testId}
        className={`group w-full flex items-center justify-between px-3 py-2.5 rounded-md text-sm font-semibold transition-colors ${
          childActive
            ? "bg-teal-50 text-teal-800 border border-teal-100"
            : "text-slate-600 hover:bg-slate-50 hover:text-slate-900 border border-transparent"
        }`}
      >
        <span className="flex items-center gap-3">
          <Icon size={17} />
          {item.label}
        </span>
        <ChevronDown
          size={14}
          className={`opacity-50 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open ? (
        <div className="mt-1 ml-5 pl-3 border-l border-slate-200 space-y-1" data-testid={`${item.testId}-children`}>
          {item.children.map((c) => {
            const CIcon = c.icon;
            return (
              <NavLink
                key={c.to}
                to={c.to}
                data-testid={c.testId}
                onClick={onNavigate}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-[13px] font-medium transition-colors ${
                    isActive
                      ? "bg-teal-50 text-teal-800"
                      : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                  }`
                }
              >
                <CIcon size={15} />
                {c.label}
              </NavLink>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export function AppLayout({ children }) {
  const { user, logout, clearSession } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [approverMeta, setApproverMeta] = useState({ is_approver: false, pending_count: 0 });
  const [idleWarnOpen, setIdleWarnOpen] = useState(false);

  const handleIdleLogout = async () => {
    setIdleWarnOpen(false);
    try {
      await logout();
    } catch {
      /* ignore */
    }
    toast.warning("Sesi berakhir karena tidak aktif selama 15 menit");
    navigate("/login", { replace: true });
  };

  const { remainingWarnMs, extendSession } = useIdleLogout({
    idleMs: 15 * 60 * 1000,
    warningMs: 60 * 1000,
    enabled: !!user,
    onWarning: () => setIdleWarnOpen(true),
    onIdle: handleIdleLogout,
  });

  const onUnauthorizedRef = useRef(null);
  onUnauthorizedRef.current = () => {
    clearSession();
    navigate("/login", { replace: true });
  };

  useEffect(() => {
    let alive = true;
    let iv = null;
    const handler = () => loadMeta();
    const stop = () => {
      alive = false;
      clearInterval(iv);
      window.removeEventListener("atlas:refresh-approvals", handler);
    };
    async function loadMeta() {
      if (!alive) return;
      try {
        const { data } = await api.get("/requests/approver-meta");
        if (alive) setApproverMeta(data);
      } catch (e) {
        if (alive && e?.response?.status === 401) {
          stop();
          onUnauthorizedRef.current();
        }
      }
    }
    loadMeta();
    iv = setInterval(loadMeta, 30000);
    window.addEventListener("atlas:refresh-approvals", handler);
    return stop;
  }, []);

  const isAdmin = user?.role === "admin";
  const isPayrollAdmin = user?.role === "payroll_admin";
  const isAtasanUnit = ATASAN_UNIT_ROLES.has(user?.role);
  const isLeader = LEADER_ROLES.has(user?.role);
  const showAdmin = isAdmin;

  // Fase 7 — Hide Menu policy:
  // - Admin: sembunyikan Riwayat, Cuti & Izin, Lembur, Klaim Pengobatan
  //   (admin fokus ke administrasi, tidak perlu modul karyawan).
  // - Pimpinan (asisten_manager/manager/general_manager/kepala_divisi/direksi):
  //   sembunyikan menu Lembur (mereka hanya memberi Perintah Lembur, bukan
  //   menerima).
  const employeeItems = NAV_EMPLOYEE.filter((n) => {
    // Admin & Payroll Admin adalah pengelola sistem — tidak melakukan check-in
    // dan tidak mengisi biodata pribadi. Sembunyikan menu "Hari Ini" & "Biodata Saya".
    if ((isAdmin || isPayrollAdmin) && ["/", "/biodata"].includes(n.to)) return false;
    if (isAdmin && ["/riwayat", "/cuti-izin", "/lembur", "/klaim", "/slip-gaji"].includes(n.to)) return false;
    if (isPayrollAdmin && ["/riwayat", "/cuti-izin", "/lembur", "/klaim"].includes(n.to)) return false;
    if (HIDE_LEMBUR_ROLES.has(user?.role) && n.to === "/lembur") return false;
    return true;
  }).map((n) =>
    n.key === "lembur" && approverMeta.pending_perintah_lembur
      ? { ...n, badge: approverMeta.pending_perintah_lembur }
      : n
  );

  const items = [
    ...employeeItems,
    ...(approverMeta.is_approver
      ? [{
          to: "/persetujuan",
          label: "Persetujuan",
          icon: ClipboardCheck,
          testId: "nav-persetujuan",
          badge: approverMeta.pending_count,
        }]
      : []),
    ...(isLeader && !isAdmin
      ? [{
          to: "/perintah-lembur",
          label: "Perintah Lembur",
          icon: Megaphone,
          testId: "nav-perintah-lembur",
        }]
      : []),
    // SPPD — semua role boleh akses (admin + leader + pegawai) karena pegawai
    // perlu akses untuk isi Section 4/7, Section 6 (log perjalanan), dan unduh PDF.
    {
      to: "/perintah-sppd",
      label: "Perjalanan Dinas",
      icon: Plane,
      testId: "nav-perintah-sppd",
      ...(approverMeta.pending_perintah_sppd ? { badge: approverMeta.pending_perintah_sppd } : {}),
    },
    ...(isAtasanUnit ? NAV_ATASAN : []),
    ...(isPayrollAdmin ? NAV_PAYROLL_ADMIN : []),
    ...(showAdmin ? NAV_ADMIN : []),
  ];

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="app-layout">
      {/* Sidebar — desktop */}
      <aside className="hidden lg:flex w-72 flex-col border-r border-slate-200 bg-white sticky top-0 h-screen">
        <div className="p-6 border-b border-slate-100">
          <Logo />
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {items.map((it) => {
            if (it.children) return <NavGroup key={it.testId} item={it} />;
            const Icon = it.icon;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                data-testid={it.testId}
                className={({ isActive }) =>
                  `group flex items-center justify-between px-3 py-2.5 rounded-md text-sm font-semibold transition-colors ${
                    isActive
                      ? "bg-teal-50 text-teal-800 border border-teal-100"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900 border border-transparent"
                  }`
                }
              >
                <span className="flex items-center gap-3">
                  <Icon size={17} />
                  {it.label}
                  <CountBadge count={it.badge} />
                </span>
                <ChevronRight size={14} className="opacity-40 group-hover:opacity-100" />
              </NavLink>
            );
          })}
        </nav>
        <div className="p-4 border-t border-slate-100">
          <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-1">
              Masuk sebagai
            </div>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-sm font-semibold text-slate-900 leading-tight" data-testid="sidebar-user-name">
                  {user?.name}
                </div>
                <div className="text-[11px] text-slate-500 leading-tight mt-0.5">
                  {user?.npp}
                </div>
              </div>
              <RoleBadge role={user?.role} />
            </div>
            <NavLink
              to="/profil"
              data-testid="nav-profil"
              className="mt-3 w-full text-xs font-semibold text-teal-800 hover:text-teal-900 flex items-center justify-center gap-1.5 py-2 rounded-md border border-teal-100 hover:bg-teal-50 transition-colors"
            >
              <UserCog size={13} /> Profil &amp; Ganti Password
            </NavLink>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="mt-2 w-full text-xs font-semibold text-rose-700 hover:text-rose-900 flex items-center justify-center gap-1.5 py-2 rounded-md border border-rose-100 hover:bg-rose-50 transition-colors"
            >
              <LogOut size={13} /> Keluar
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 teal-glass border-b border-slate-200">
        <div className="flex items-center justify-between px-4 py-3">
          <Logo size={30} />
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 rounded-md text-slate-700"
            data-testid="mobile-menu-open"
          >
            <Menu size={20} />
          </button>
        </div>
      </div>

      {/* Mobile menu overlay */}
      {mobileOpen ? (
        <div className="lg:hidden fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-slate-900/40"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute right-0 top-0 bottom-0 w-80 max-w-[85%] bg-white border-l border-slate-200 flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <Logo size={30} />
              <button
                onClick={() => setMobileOpen(false)}
                className="p-2 rounded-md text-slate-600"
                data-testid="mobile-menu-close"
              >
                <X size={18} />
              </button>
            </div>
            <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
              {items.map((it) => {
                if (it.children)
                  return (
                    <NavGroup
                      key={it.testId}
                      item={it}
                      onNavigate={() => setMobileOpen(false)}
                    />
                  );
                const Icon = it.icon;
                const active = location.pathname === it.to || (it.to !== "/" && location.pathname.startsWith(it.to));
                return (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-3 rounded-md text-sm font-semibold ${
                      active ? "bg-teal-50 text-teal-800 border border-teal-100" : "text-slate-700"
                    }`}
                    data-testid={`m-${it.testId}`}
                  >
                    <Icon size={17} /> {it.label}
                    <CountBadge count={it.badge} />
                  </NavLink>
                );
              })}
            </nav>
            <div className="p-4 border-t border-slate-100">
              <div className="text-xs text-slate-500 mb-1">{user?.name}</div>
              <NavLink
                to="/profil"
                onClick={() => setMobileOpen(false)}
                data-testid="m-nav-profil"
                className="w-full text-sm font-semibold text-teal-800 hover:text-teal-900 py-2.5 mb-2 rounded-md border border-teal-100 hover:bg-teal-50 flex items-center justify-center gap-2"
              >
                <UserCog size={14} /> Profil &amp; Ganti Password
              </NavLink>
              <button
                onClick={handleLogout}
                className="w-full text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 py-2.5 rounded-md flex items-center justify-center gap-2"
                data-testid="m-logout-btn"
              >
                <LogOut size={14} /> Keluar
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Content */}
      <main className="flex-1 min-w-0 pt-14 lg:pt-0">{children}</main>

      {/* Idle warning modal — Fase 7: auto-logout 15 menit, warning 1 menit */}
      <Dialog open={idleWarnOpen} onOpenChange={() => { /* forced modal */ }}>
        <DialogContent className="max-w-md" data-testid="idle-warning-dialog" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Sesi Akan Berakhir</DialogTitle>
            <DialogDescription>
              Anda tidak aktif dalam waktu yang lama. Sesi akan otomatis diakhiri dalam{" "}
              <b>{Math.max(0, Math.ceil((remainingWarnMs ?? 60000) / 1000))} detik</b>.
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs text-slate-500">
            Klik "Lanjutkan Sesi" untuk tetap masuk. Jika tidak ada tindakan, Anda akan otomatis logout.
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setIdleWarnOpen(false); handleIdleLogout(); }}
              data-testid="idle-logout-now-btn"
            >
              Logout Sekarang
            </Button>
            <Button
              onClick={() => { setIdleWarnOpen(false); extendSession(); }}
              className="bg-teal-700 hover:bg-teal-800"
              data-testid="idle-extend-btn"
            >
              Lanjutkan Sesi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AppLayout;
